import { FunctionComponent } from 'react';
import { UserGroupPurchaseLimitsCard, UserGroupPurchaseLimitsEditModal } from '@cdaas/reusable-components-daas';
import { useTranslation } from 'react-i18next';
import { OrganizationWithUserGroupsResponse, UserGroupRequest, UserGroupResponse } from '@cdaas/api-clients/dist/asset-admin-openapi';

interface UserGroupPurchaseLimitsProps {
  userGroup: UserGroupResponse;
  organization: OrganizationWithUserGroupsResponse;
  modalVisible: boolean;
  onClickEdit: () => void;
  updateUserGroupPurchaseLimits: (values: Pick<UserGroupRequest, 'policies'>) => Promise<boolean>;
  cancelUpdateUserGroupPurchaseLimits: () => void;
}

const UserGroupPurchaseLimits: FunctionComponent<UserGroupPurchaseLimitsProps> = ({
  userGroup,
  organization,
  modalVisible,
  onClickEdit,
  updateUserGroupPurchaseLimits,
  cancelUpdateUserGroupPurchaseLimits,
}) => {
  const { t } = useTranslation('userGroups');

  return (
    <>
      <UserGroupPurchaseLimitsCard
        userGroup={userGroup}
        organization={organization}
        onEditButtonClick={onClickEdit}
        translations={{
          title: t('purchaseLimits.card.title'),
          productsVisibilityRowTitle: t('purchaseLimits.card.productsVisibilityRowTitle'),
          hideProductsInfoText: t('purchaseLimits.card.hideProductsInfoText'),
          subscriptionProductsRowTitle: t('purchaseLimits.card.subscriptionProductsRowTitle'),
          singleDevicePolicyInfoText: t('purchaseLimits.card.singleDevicePolicyInfoText'),
          noLimitsInfoText: t('purchaseLimits.card.noLimitsInfoText'),
          override: t('purchaseLimits.card.override'),
          month: t('purchaseLimits.card.month'),
          summaryHideProductsInfoText: t('purchaseLimits.card.summaryHideProductsInfoText'),
          summaryDontHideProductsInfoText: t('purchaseLimits.card.summaryDontHideProductsInfoText'),
          summarySingleDevicePolicyInfoText: t('purchaseLimits.card.summarySingleDevicePolicyInfoText'),
          summaryNoSingleDevicePolicyInfoText: t('purchaseLimits.card.summaryNoSingleDevicePolicyInfoText'),
          summaryNoLimitsInfoText: t('purchaseLimits.card.summaryNoLimitsInfoText'),
          editButton: t('purchaseLimits.card.editButton'),
        }}
      />
      {modalVisible && (
        <UserGroupPurchaseLimitsEditModal
          userGroup={userGroup}
          organization={organization}
          onSave={updateUserGroupPurchaseLimits}
          onCancel={cancelUpdateUserGroupPurchaseLimits}
          translations={{
            modalHeading: t('purchaseLimits.editModal.modalHeading'),
            generalInfo: t('purchaseLimits.editModal.generalInfo'),
            productsVisibilityTitle: t('purchaseLimits.editModal.productsVisibilityTitle'),
            procurementLimitOverrideCheckboxLabel: t('purchaseLimits.editModal.procurementLimitOverrideCheckboxLabel'),
            procurementLimitInputLabel: t('purchaseLimits.editModal.procurementLimitInputLabel', {
              currency: organization.locale.currency.toUpperCase(),
            }),
            procurementLimitValueNotSetError: t('purchaseLimits.editModal.procurementLimitValueNotSetError'),
            procurementLimitValueNotNumberError: t('purchaseLimits.editModal.procurementLimitValueNotNumberError'),
            month: t('purchaseLimits.editModal.month'),
            procurementLimitSetInfo: t('purchaseLimits.editModal.procurementLimitSetInfo'),
            procurementLimitNotSetInfo: t('purchaseLimits.editModal.procurementLimitNotSetInfo'),
            saveButton: t('purchaseLimits.editModal.saveButton'),
            cancelButton: t('purchaseLimits.editModal.cancelButton'),
            saveApiErrorTitle: t('purchaseLimits.editModal.saveApiErrorTitle'),
            saveApiErrorMessage: t('purchaseLimits.editModal.saveApiErrorMessage'),
          }}
        />
      )}
    </>
  );
};

export default UserGroupPurchaseLimits;
